// Mount js/login_modal/LoginModalManager and open it, reliably.
//
// NO DYNAMIC IMPORT IN THIS FILE, on purpose: ./lazyMountLoginModal holds the
// `import()`, which jest 27 cannot execute. Keeping the protocol here is what
// makes it testable — do not move logic into that file.
//
// THE RACE THIS EXISTS FOR. LoginModalManager registers its 'open-login-modal'
// listener inside a useEffect, and React 18 flushes passive effects on its own
// timeline, not after any fixed number of tasks. A dispatch that lands first is
// dropped in SILENCE (a CustomEvent with no listener is a no-op) and the download
// button does nothing. A one-macrotask wait shipped once and failed exactly this
// way on a real page.
//
// So: dispatch, then verify an open <dialog> in our container, and re-dispatch on
// a bounded schedule until it appears. Re-dispatch is safe — the manager
// re-applies the same detail. DO NOT replace this with a fixed timeout; that is
// the bug that already happened. Success is an observable end state, so a React
// scheduling change costs an iteration, not the arm, and the deadline REJECTS so
// the caller falls back to control's redirect rather than a dead button.

type ModalRoot = { render: (node: unknown) => void };

export type LoginModalDeps = {
  LoginModalManager: unknown;
  openLoginModal: (detail: unknown) => void;
  createElement: (type: unknown) => unknown;
  createRoot: (container: Element) => ModalRoot;
};

export type OpenOptions = {
  // Injected by the suite so the protocol can be exercised without real timers.
  now?: () => number;
  delay?: (fn: () => void, ms: number) => void;
  retryMs?: number;
  deadlineMs?: number;
};

const CONTAINER_CLASS = 'js-profile-signup-wall-modal-root';
// showModal() reflects the `open` attribute, so this is proof the manager
// received the event and committed.
const OPEN_DIALOG = 'dialog[open]';

const DEFAULT_RETRY_MS = 25;
// A fallback deadline, not a latency budget. At 25ms per retry this is still ~60
// attempts, far more than any plausible React commit needs, and crossing it means
// something is genuinely wrong — at which point redirecting to the signup page
// beats making the visitor watch a dead button. Was 4000ms, which on a slow
// connection left the Download button looking broken for four seconds on the one
// surface whose proven lever is download emphasis.
const DEFAULT_DEADLINE_MS = 1500;

let container: HTMLElement | null = null;

function mountOnce(deps: LoginModalDeps): HTMLElement {
  if (container != null && container.isConnected) return container;

  container = document.createElement('div');
  container.className = CONTAINER_CLASS;
  document.body.appendChild(container);
  deps.createRoot(container).render(deps.createElement(deps.LoginModalManager));

  return container;
}

/**
 * Mount the login modal if it is not mounted, then open it with `detail`,
 * resolving only once the modal is actually open. Rejects if it never opens
 * within the deadline, so the caller can fall back.
 */
export function openLoginModalWhenReady(
  deps: LoginModalDeps,
  detail: unknown,
  options: OpenOptions = {},
): Promise<void> {
  const now = options.now ?? (() => Date.now());
  const delay =
    options.delay ??
    ((fn: () => void, ms: number) => {
      setTimeout(fn, ms);
    });
  const retryMs = options.retryMs ?? DEFAULT_RETRY_MS;
  const deadlineMs = options.deadlineMs ?? DEFAULT_DEADLINE_MS;

  const root = mountOnce(deps);
  const startedAt = now();

  return new Promise<void>((resolve, reject) => {
    const attempt = (): void => {
      deps.openLoginModal(detail);

      // Checked after a delay, not synchronously: React commits the state change
      // on its own schedule, so a slow commit just costs one more loop.
      delay(() => {
        if (root.querySelector(OPEN_DIALOG) != null) {
          resolve();

          return;
        }

        if (now() - startedAt >= deadlineMs) {
          reject(new Error('login modal did not open before the deadline'));

          return;
        }

        attempt();
      }, retryMs);
    };

    attempt();
  });
}

// Test seam: the container is module state (one modal per page).
export function resetLoginModalMountForTest(): void {
  container?.remove();
  container = null;
}

export default openLoginModalWhenReady;
